import { useEffect, useState } from 'react';
import {
  type AuthApp,
  SOCIAL_SIGN_IN_PROVIDERS,
  type SocialSignInProvider,
  workerBaseUrl,
} from '../../lib/betterAuthClient';

/**
 * Which social sign-in buttons this deployment can actually complete.
 *
 * Asked of the backend rather than read from a build variable: the credentials live in the
 * Worker's environment, so anything else is a second copy of the same fact that goes stale the
 * first time one is changed without the other — and the failure mode is a button that sends
 * someone to a broken consent screen.
 *
 * Starts empty and fills in, so a slow or failed request shows the password form alone rather
 * than an action that cannot finish.
 */
export function useSocialSignInProviders(app: AuthApp): SocialSignInProvider[] {
  const [providers, setProviders] = useState<SocialSignInProvider[]>([]);

  useEffect(() => {
    let cancelled = false;

    fetch(`${workerBaseUrl()}/api/auth/${app}/social-providers`)
      .then((response) => (response.ok ? response.json() : { providers: [] }))
      .then((body: unknown) => {
        if (cancelled) return;
        const returned =
          body &&
          typeof body === 'object' &&
          Array.isArray((body as { providers?: unknown }).providers)
            ? ((body as { providers: unknown[] }).providers as unknown[])
            : [];
        // Ordered by our own list, not the server's, so the buttons never reshuffle between
        // deployments; filtered by it so an unknown name can never render a dead button.
        setProviders(SOCIAL_SIGN_IN_PROVIDERS.filter((provider) => returned.includes(provider)));
      })
      .catch(() => {
        if (!cancelled) setProviders([]);
      });

    return () => {
      cancelled = true;
    };
  }, [app]);

  return providers;
}
